'use client';

import React, { FC, PropsWithChildren, useMemo } from 'react';

import { Product } from '@ordering/gql/graphql';
import { useStreamingTranslations } from '@ordering/hooks/useStreamingTranslations/useStreamingTranslations';

type ProductsContextValue = {
  products: Product[];
};

const ProductsContext = React.createContext<ProductsContextValue>({} as ProductsContextValue);

const TRANSLATABLE_PATHS = [
  'name',
  'displayName',
  'description',
  'category.name',
  'variants.name',
  'variants.modifiers.name',
  'variants.modifiers.variants.name',
];

export const ProductContextProvider: FC<PropsWithChildren<{ products: Product[] }>> = ({ products, children }) => {
  const s = useStreamingTranslations();
  const value = useMemo(() => ({ products: s(products, TRANSLATABLE_PATHS) }), [s, products]);
  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

export const useProductsContext = () => {
  const context = React.useContext(ProductsContext);
  if (!context) {
    throw new Error('useProductsContext must be used within a ProductsContextProvider');
  }
  return context;
};
